<template>
  <div class="sidebar-tracking">
    <b-tabs
      align="center"
      class="tracking-tabs"
      pills
    >
      <b-tab
        title-item-class="flex-fill text-center"
        active
        @click="tabLeft"
      >
        <template #title>
          <i
            v-testing="'processes-tab'"
            class="far fa-list-ul"
            aria-hidden="true"
          />
        </template>
        <div class="scrollable">
          <div class="scroll-sidebar">
            <div
              v-if="loadingSidebar"
              class="empty-state-sidebar d-flex flex-column text-center p-5 align-items-center"
            >
              <vt-loading />
            </div>
            <template v-else>
              <div
                v-if="!tasks.length"
                class="no-list px-3 d-flex flex-column"
              >
                <span class="title mb-3 white">{{ $t('laboratory.tracking.noActiveProcesses') }}</span>
                <div
                  class="d-flex"
                  style="opacity: .2"
                >
                  <div class="no-list-circle" />
                  <div class="no-list-card" />
                </div>
                <div
                  class="d-flex"
                  style="opacity: .18"
                >
                  <div class="no-list-circle" />
                  <div class="no-list-card" />
                </div>
                <div
                  class="d-flex"
                  style="opacity: .16"
                >
                  <div class="no-list-circle" />
                  <div class="no-list-card" />
                </div>
                <div
                  class="d-flex"
                  style="opacity: .14"
                >
                  <div class="no-list-circle" />
                  <div class="no-list-card" />
                </div>
                <div
                  class="d-flex"
                  style="opacity: .1"
                >
                  <div class="no-list-circle" />
                  <div class="no-list-card" />
                </div>
              </div>
              <div v-if="tasks.length > 1">
                <div
                  v-for="(task, indexTask) in tasks"
                  :key="task.id"
                  class="elements pl-2"
                  :class="{'active': rowActive === task.id}"
                >
                  <b-button
                    :id="task.id.toString()"
                    class="element-btn py-2 d-flex align-items-center"
                    block
                    @click="setSelectedOption(task)"
                  >
                    <div
                      v-if="summaryTasks"
                      class="circle d-flex align-items-center justify-content-center animate__animated animate__fadeIn animate__faster"
                      style="position:relative"
                      :class="{'pending': summaryTasks[task.operation] > 0}"
                      :style="{ 'animation-delay': (indexTask) * .025+'s'}"
                    >
                      <span
                        :key="summaryTasks[task.operation]"
                        class="animate__animated animate__fadeIn animate__faster"
                        :style="{ 'animation-delay': (indexTask) * .005+'s' }"
                        style="text-align:center"
                      >
                        {{ getCountFromTaskOperation(task.operation) }}
                      </span>

                      <div
                        v-if="defaultOption === task.id"
                        class="lock d-flex align-items-center justify-content-center"
                      >
                        <i
                          class="far fa-thumbtack"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <span class="ml-3">{{ task.label }}</span>
                  </b-button>
                  <div class="line-tracking" />
                </div>
              </div>
            </template>
          </div>
        </div>
      </b-tab>
      <b-tab
        title-item-class="flex-fill text-center"
        @click="tabRight"
      >
        <template #title>
          <div style="position:relative">
            <i
              v-testing="'sections-tab'"
              class="far fa-cog"
              aria-hidden="true"
            />
            <b-badge
              class="indicator-bubble"
              pill
              style="position:absolute;top:2px;margin-left:2px;"
            >
              {{ checkedCount }}
            </b-badge>
          </div>
        </template>
        <sections-sidebar-tracking
          :sections="trackingSections"
          @sectionChanged="sectionsUpdated()"
        />
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import SectionsSidebarTracking from '@/modules/laboratoryModule/laboratoryTracking/sidebarTracking/SectionsSidebarTracking'
import LaboratoryStateMixin from '@/modules/laboratoryModule/mixins/laboratoryStateMixin'
import sectionsMixin from '@/modules/laboratoryModule/mixins/sectionsMixin'
import tasksMixin from '@/modules/laboratoryModule/mixins/tasksMixin'
import laboratorySidebarStore from '@/modules/laboratoryModule/sideBar/laboratorySideBarStore'

export default {
    name: 'SidebarTracking',
    components: {
        SectionsSidebarTracking
    },
    mixins: [
        LaboratoryStateMixin,
        sectionsMixin,
        tasksMixin
    ],
    data() {
        return {
            lockActive: false,
            showCollapse: true,
            rowActive: null,
            trackingSections: [],
            loadingSidebar: true
        }
    },
    computed: {
        labId() {
            return this.$store.getters.labId
        },
        defaultOption() {
            return this.state.config.defaultOptionId
        },
        checkedCount() {
            return this.trackingSections.filter(section => section.checked).length
        }
    },
    watch: {
        selectedOption() {
            this._activateRow()
        }
    },
    async mounted() {
        await this.loadSidebarCustomConfig()
        await this._updateSections()

        this.loadingSidebar = false
        this.$eventHub.$on('updateTaskCount', this.onLoadTaskCount)
    },
    beforeDestroy() {
        this.$eventHub.$off('updateTaskCount', this.onLoadTaskCount)
    },
    methods: {
        onLoadTaskCount() {
            this._loadTaskCount()
        },
        tabLeft() {
            this.$emit('tabToggle', false)
        },
        tabRight() {
            this.$emit('tabToggle', true)
        },
        getCountFromTaskOperation(taskOperation) {
            const count = this.summaryTasks[taskOperation]
            if (count > 999) {
                return '999+'
            } else if (count > 0) {
                return count
            }
            return ''
        },
        setSelectedOption(option) {
            laboratorySidebarStore.setSelectedOption(option)
        },
        _activateRow() {
            this.rowActive = this.selectedOption?.id ?? null
        },
        async sectionsUpdated() {
            this.loadingSidebar = true

            await this._updateSections()
            await this.saveSidebarCustomConfig()

            this.loadingSidebar = false
        },
        async _updateSections() {
            this.trackingSections = this.sections.filter(section => section.allowsTouchMode && !section.isCytology)
            this.$eventHub.$emit('trackingSectionsUpdated', this.trackingSections)

            const filteredSections = this.trackingSections.filter(section => section.checked).map(section => section.id)
            laboratorySidebarStore.setSectionIds(filteredSections)
            await this._getTasks(filteredSections)

            if (filteredSections.length) {
                await laboratorySidebarStore.loadConfig()
                if (!this.selectedOption && this.defaultOption) {
                    laboratorySidebarStore.selectDefaultOption()
                }
                this._activateRow()
            } else {
                this.setSelectedOption(null)
            }
            this.$forceUpdate()
        },
        async _getTasks(sectionIds) {
            this.rowActive = null

            await Promise.allSettled([
                this.loadTasks(sectionIds),
                this.loadSummaryTasks(sectionIds)
            ])
        },
        async _loadTaskCount() {
            await this.loadSummaryTasks(this.sectionIds)
        }
    }
}
</script>

<style lang="scss">
.sidebar__tracking ::-webkit-scrollbar-track{
    border-radius: 0;
}

.sidebar__tracking ::-webkit-scrollbar-thumb{
    border-radius: 0 !important;
    background: vt-color(sidebar-childs-bg, .6);
}

.sidebar__tracking ::-webkit-scrollbar{
    width: 6px !important;
    height: 6px !important;
    background: vt-color(white-light);
}

.sidebar__tracking .double-bounce1,
.sidebar__tracking .double-bounce2 {
    background-color: vt-color(white-light) !important;
}

.sidebar__tracking .line-tracking {
    width: 2px;
    height: 100%;
    background: vt-color(white-light, .8);
    position: absolute;
    left: 32.5px;
    top: 0;
}

.sidebar__tracking .elements:first-child .line-tracking {
    top: 20px;
    height: 100%;
}

.sidebar__tracking .elements:last-child .line-tracking {
    top: 0;
    height: 20px;
}

.sidebar__tracking .elements.active .line-tracking {
    display: none;
}

.sidebar__tracking {
  background: vt-color(sidebar-childs-bg);
  box-shadow: 1px 0 5px 0 #b3b3b3;
  z-index: 10;
  height: calc(100vh - 50px);
}

.sidebar__tracking .tracking-tabs .nav-pills .nav-link.active,
.nav-pills .show > .nav-link {
  background-color: transparent;
  color: vt-color(primary);
  border-radius: 0;
}

.sidebar__tracking .tracking-tabs .nav-link.active {
  position: relative;
}

.sidebar__tracking .tracking-tabs .nav-link.active i {
  color: vt-color(white-light);
}

.sidebar__tracking .tracking-tabs .nav-link.active:before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  border-radius: 2px 2px 0 0;
  width: 70%;
  height: 5px;
  background: vt-color(white-light);
  bottom: 0;
}

.sidebar__tracking .nav-item {
  border-bottom: 0.5px solid vt-color(sidebar-section-header-bg);
}

.sidebar__tracking .nav-item i {
  color: vt-color(base-text);
  font-size: font-size(big);
}

.sidebar__tracking .nav-link {
  padding: 0.7rem 1rem;
}
</style>

<style scoped lang="scss">

.sidebar__tracking .no-list{
  margin-top: 10%;
}

.sidebar__tracking .no-list .title{
  font-size: font-size(very-normal);
}

.sidebar__tracking .no-list .no-list-card{
  width: 80%;
  height: 50px;
  border: 1px solid grey;
  border-radius: 5px;
  margin-top: 10px;
}

.sidebar__tracking .no-list .no-list-circle{
  width: 50px;
  height: 50px;
  border: 1px solid vt-color(base-text);
  border-radius: 50%;
  margin-top: 10px;
  margin-right: 10px;
}

.sidebar__tracking .lock{
  position: absolute;
  right: -10px;
  top: 16px;
  width: 20px;
  height: 20px;
  border: 1px solid vt-color(base-text);
  border-radius: 50%;
  background:white;
  font-size: 12px;
  color: grey;
}

.sidebar__tracking .section-card {
  border: 1px solid #d8d8d8;
  border-radius: 0.25rem;
  cursor: pointer;
}

.sidebar__tracking .section-card.active {
  border: 1px solid vt-color(primary);
  background: vt-color(primary);
}

.sidebar__tracking .section-card.active span {
  color: vt-color(white-light);
}

.sidebar__tracking .section-card span {
  color: vt-color(dark-text);
}

/*--Active elements--*/

.elements {
  transition: 0.3s;
  padding: 3px 0;
  position: relative;
}

.elements.active {
  background: vt-color(sidebar-childs-bg);
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  bottom: 0;
  z-index: 2;
}

.elements.active span {
  color: vt-color(sidebar-parent-font);
}

.elements .circle {
  transition: 0.3s;
}

/*--Circle states--*/

.elements .circle.pending {
  border-color: #fc6274;
  color: #FFF;
  background-color: #fc6274;
}

/*-- General--*/

.scroll-sidebar {
  overflow-y: auto;
  height: calc(100vh - 93px);
}

.section .section-btn,
.section .section-btn:active {
  border-radius: 0;
  background: vt-color(base-text);
  border: transparent;
  border-bottom: 1px solid #d8d8d8;
  color: vt-color(white-light);
  text-align: left;
  font-size: 1rem;
}

.section .section-btn i {
  font-size: 1.2rem;
}

.scroll-sidebar .element-btn {
  background: transparent !important;
  border-radius: 0;
  border: none;
  color: vt-color(sidebar-parent-font);
}

.scroll-sidebar .element-btn span {
  font-size: font-size(very-small);
  text-align: left;
  width: 80%;
}

.scroll-sidebar .element-btn span::-webkit-scrollbar {
    display: none;
}

.section .section-content {
  position: relative;
}

.section .section-content .line {
  background: #cccccc;
  width: 3px;
  height: 10px;
  position: absolute;
  left: 35px;
  top: 10px;
}

.element-btn .circle {
  border: 2px solid vt-color(primary);
  color: #FFF;
  background-color: vt-color(primary);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: font-size(small);
  z-index: 1;
}

.setup {
  border-bottom: 1px solid #d8d8d8;
}

.lab {
  border-bottom: 1px solid #d8d8d8;
  background: vt-color(base-text);
  color: #fff;
}

.lab .setup-icon {
  font-size: 18px;
}

.menu-main-title h4 {
  font-size: 1rem;
}

.menu-main-title small {
  font-size: 0.7rem;
  text-transform: uppercase;
}

.menu-main-title {
  line-height: 0.9rem;
}

.menu-module {
  display: flex;
  border-bottom: 1px solid #d8d8d8;
  align-items: center;
  border-left: 1rem solid vt-color(primary);
  height: 48px;
}

.section-pd {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  width: 100%;
  margin: 0;
}

.icon-wrapper-main {
  font-size: 1.25rem;
}

.sidebar-tracking .branding {
  background: linear-gradient(to right, vt-color(primary), #76a1ff);
}

.sidebar-tracking .branding img {
  width: 70%;
}

/*--LAPTOP--*/
@media only screen and (min-device-width: 768px) and (max-device-width: 1366px) {
  .sidebar-tracking {
    width: 268px;
  }
}

/*--Landscape--*/
@media only screen and (min-device-width: 1281px) and (max-device-width: 2000px) {
  .sidebar-tracking {
    width: 268px;
  }
}

/*--Touch and 4:3--*/
@media only screen and (min-device-width: 1080px) and (max-device-width: 1920px) {
  .sidebar-tracking {
    width: 300px;
  }
}
</style>
